import { getApps, initializeApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  UserCredential,
  setPersistence,
  browserLocalPersistence,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";

// If you want Firestore, you can import it as well
// import { getFirestore } from "firebase/firestore";

/* -------------------------------------------
  1. Initialize or retrieve existing Firebase app
-------------------------------------------- */
let app;
if (!getApps().length) {
  try {
    const deploymentEnv = process.env.REACT_APP_DEPLOYMENT_ENV; // "prod" or "dev"
    // Log which configuration is selected (masking sensitive parts)
    console.log("Environment: ", deploymentEnv);
    try {
      app = initializeApp({
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
        measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
      });
    } catch (e) {
      console.error("Error initializing Firebase", e);
    }
  } catch (e) {
    console.error("Error initializing Firebase", e);
  }
} else {
  // If already initialized, use the first app
  app = getApps()[0];
}

if (!app) {
  app = getApps()[0];
}

/* -------------------------------------------
  2. Export Firebase services
-------------------------------------------- */
export const functions = getFunctions(app);
export const db = getFirestore(app);
export const auth = getAuth(app);
// Set the persistence to local so that sessions persist across tab closes
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    // console.log("Firebase Auth persistence set to local!");
  })
  .catch((error) => {
    console.error("Error setting Firebase Auth persistence:", error);
  });
// Optional: connect to emulators locally if needed
// import { connectFunctionsEmulator } from "firebase/functions";
// import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
// if (process.env.NODE_ENV === "development") {
//   connectFunctionsEmulator(functions, "localhost", 5001);
//   connectFirestoreEmulator(getFirestore(app), "localhost", 8080);
// }

const googleProvider = new GoogleAuthProvider();

/* -------------------------------------------
  3. Helper to call Cloud Function "createNewUser"
-------------------------------------------- */
async function createUserProfileServer(
  uid: string,
  email: string | null | undefined,
  provider: string | null | undefined,
  partner = ""
) {
  // "createNewUser" must match your function name in Cloud Functions
  const createNewUser = httpsCallable(functions, "createNewUser");
  try {
    const result = await createNewUser({ uid, email, provider, partner });
    console.log("createNewUser response:", result);
  } catch (error) {
    console.error("Error calling createNewUser:", error);
    // handle or rethrow the error as needed
  }
}

/* -------------------------------------------
  4. Implement Auth Functions
-------------------------------------------- */

/** Sign in with email/password */
export async function signInWithEmailPwd(
  email: string,
  password: string,
  partner = ""
) {
  try {
    // Set the persistence for the session
    await setPersistence(auth, browserLocalPersistence);

    // Sign in the user
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );

    // Reload the user to get the latest emailVerified status
    await userCredential.user.reload();
    // console.log("User reloaded:", userCredential.user);
    // console.log("Partner:", partner);
    // Check if the email is verified
    if (!userCredential.user.emailVerified && partner === "") {
      // console.log("Email not verified");
      // Sign out the user if not verified
      signOut(auth);
      throw new Error(
        "Your email is not verified yet. Please verify your email first."
      );
    }

    try {
      // Attempt to create/update user profile
      await createUserProfileServer(
        userCredential.user.uid,
        userCredential.user.email ?? "",
        "password",
        partner
      );
    } catch (profileError) {
      // If profile creation fails, sign out the user and throw error
      await signOut(auth);
      console.error("Profile creation failed:", profileError);
      throw new Error(
        "Failed to setup user profile. Please try again or contact support."
      );
    }

    // Return the signed-in user's credential only if everything succeeds
    return userCredential;
  } catch (error: any) {
    // Handle Firebase auth-specific errors
    console.log("Error code:", error.code);
    if (error.code) {
      switch (error.code) {
        case "auth/invalid-credential":
          throw new Error(
            "Invalid email or password, please sign up first or double check your password"
          );
        case "auth/user-not-found":
          throw new Error("User not found. Please sign up first.");
        case "auth/wrong-password":
          throw new Error("Invalid email or password");
        case "auth/invalid-email":
          throw new Error("Invalid email format");
        case "auth/too-many-requests":
          throw new Error("Too many failed attempts. Please try again later.");
        case "auth/user-disabled":
          throw new Error(
            "This account has been disabled. Please contact support."
          );
        case "auth/network-request-failed":
          throw new Error("Network error. Please check your connection.");
        default:
          // For any other Firebase auth errors, throw the original error message
          throw new Error(error.message);
      }
    }

    // If it's already a custom error (like our email verification error), just rethrow it
    throw error;
  }
}
/** Sign up (register) with email/password */
export async function signUpWithEmailPwd(email: string, password: string) {
  //   await setPersistence(auth, browserSessionPersistence);
  await setPersistence(auth, browserLocalPersistence);

  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );
  await sendEmailVerification(userCredential.user);
  await signOut(auth);
  // For Email/Password, the "providerId" is usually "password"
  // await handleNewUserProfile(userCredential, "password");

  return userCredential;
}

// /**
//  * Called AFTER the user has verified their email.
//  * 1) Make sure the user is actually signed in (user is non-null).
//  * 2) Reload user from Firebase to get the latest status.
//  * 3) If verified, continue; if not, throw an error.
//  * 4) Create the user profile (or any other post-verification steps).
//  */
// export async function finalizeSignUpIfVerified(partner = "") {
//     const user = auth.currentUser;
//     if (!user) {
//         throw new Error(
//             "No user is currently signed in. Please sign in first."
//         );
//     }

//     // Reload user to get the latest emailVerified status
//     await user.reload();

//     if (!user.emailVerified) {
//         throw new Error(
//             "Your email is not verified yet. Please verify your email first."
//         );
//     }

//     // Now that the user is verified, handle the new user profile
//     // (In your own code, you might pass additional info to `handleNewUserProfile`.)
//     const defaultProvider = "password";
//     const { uid, email } = user;
//     const provider = user.providerData[0]?.providerId || defaultProvider;

//     // If your user doesn't have an email (rare for Google), fallback to an empty string
//     await createUserProfileServer(uid, email ?? "", provider, partner);

//     return "User is verified, profile has been created successfully.";
// }

/** Reset password */
export async function resetPassword(email: string) {
  return sendPasswordResetEmail(auth, email);
}

/** Sign out */
export async function signOutUser() {
  return signOut(auth);
}

/** Sign in with Google (also used for sign-up if first time) */
// ***** Ensure the OAuth request includes the required Gmail scopes (e.g. Gmail read/write scopes), access_type=offline (to get a refresh token), and prompt=consent (to force selecting an account and consent).
export async function signInWithGoogle(partner = "") {
  // console.log("Signing in with Google...");
  // console.log("Partner:", partner);
  //   // Ensure the session only persists for the current browser tab/session
  //   await setPersistence(auth, browserSessionPersistence);

  //   const googleProvider = new GoogleAuthProvider();

  //   return credential;
  await setPersistence(auth, browserLocalPersistence);
  const googleProvider = new GoogleAuthProvider();
  //   googleProvider.addScope("https://www.googleapis.com/auth/gmail.readonly");
  //   googleProvider.setCustomParameters({
  //     prompt: "consent",
  //     access_type: "offline",
  //   });
  const userCredential: UserCredential = await signInWithPopup(
    auth,
    googleProvider
  );

  // Create user profile if needed.
  const { uid, email } = userCredential.user;
  await createUserProfileServer(uid, email ?? "", "google.com", partner);
  // Extract the credential.
  const credential = GoogleAuthProvider.credentialFromResult(userCredential);
  return credential;
}
